import React, { useEffect, useState } from 'react';
import { Field } from '@progress/kendo-react-form';
import { Checkbox, CheckboxChangeEvent, Switch, SwitchChangeEvent } from '@progress/kendo-react-inputs';
import styles from './CustomerConnectedServices.module.scss';
import { IConnectedService, ILookupValue } from '../../../../model';
import { connectedServicesService } from '../../../../services/api/connected-services';
import { citiesService, nexParkInstanceService, notifications } from '../../../../services';
import { FormDropDown, LookupTextFormField, requiredArrayValidator, requiredValidator } from '../../../common';
import { connectedServiceIconService } from '../../../../pages/customers/customer/connected-service-icon-service';
import { caretAltDownIcon } from '@progress/kendo-svg-icons';
import { FormDropdownMultiCheckbox } from '../../../common/forms/form-multicheckbox/FormDropdownMultiCheckbox';
import { nexResponseService } from '../../../../services/api/nexResponse';

const WARNING_SERVICE_TEXT = 'The previously saved item is no longer available. Please select an option from the list or turn off this service.';

interface CustomerConnectedServicesProps {
  edit?: boolean;
  valueGetter: (name: string) => any;
  onChange: (name: string, options: { value: any }) => void;
  customerId?: number;
  onConnectedServicesChange?: (services: IConnectedService[]) => void;
}

export const CustomerConnectedServices = (props: CustomerConnectedServicesProps) => {
  const { edit, customerId, valueGetter, onChange } = props;
  const [cities, setCities] = useState<ILookupValue[]>([]);
  const [nexParkInstances, setNexParkInstances] = useState<ILookupValue[]>([]);
  const [nexResponseRegions, setNexResponseRegions] = useState<ILookupValue[]>([]);
  const [dictionaryServices, setDictionaryServices] = useState<IConnectedService[]>([]);
  const selectedServices: IConnectedService[] = valueGetter('connectedServices') || [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const results = await Promise.allSettled([
          connectedServicesService.get(),
          customerId ? citiesService.getLookupValuesByCustomer(customerId) : citiesService.getLookupValues(),
          customerId ? nexParkInstanceService.getLookupValuesByCustomer(customerId) : nexParkInstanceService.getLookupValues(),
          nexResponseService.getRegionLookupValues(),
        ]);

        const [dictServicesResult, citiesResult, nexParkResult, nexResponseRegionsResult] = results;

        if (dictServicesResult.status === 'fulfilled') {
          setDictionaryServices(dictServicesResult.value);
        } else {
          console.error('Failed to fetch dictionary services:', dictServicesResult);
          notifications.error('Could not load services dictionary.');
        }

        if (citiesResult.status === 'fulfilled') {
          setCities(citiesResult.value);
        } else {
          console.error('Failed to fetch cities:', citiesResult);
          notifications.error('Could not load city data.');
        }

        if (nexParkResult.status === 'fulfilled') {
          setNexParkInstances(nexParkResult.value);
        } else {
          console.error('Failed to fetch NexPark instances:', nexParkResult);
          notifications.error('Could not load NexPark instances.');
        }

        if (nexResponseRegionsResult.status === 'fulfilled') {
          setNexResponseRegions(nexResponseRegionsResult.value);
        } else {
          console.error('Failed to fetch NexResponse regions:', nexResponseRegionsResult);
          notifications.error('Could not load NexResponse regions.');
        }
      } catch (e) {
        console.error('An unexpected error occurred during fetch:', e);
      }
    };

    fetchData();
  }, [customerId, edit]);

  const isSavedValueMissing = (value: any, list: ILookupValue[]): boolean => {
    const id = value?.id ?? value;

    if(id && list?.length > 0)
    {
      return !list.some((item) => String(item.id) === String(id));
    }

    return false;
  };

  const handleServiceToggle = (serviceToToggle: IConnectedService, isChecked: boolean) => {
    let updatedServices: IConnectedService[];

    if (isChecked) {
      const newSelectedService = {
        ...serviceToToggle,
        modules: [],
      };
      updatedServices = [...selectedServices, newSelectedService];
    } else {
      updatedServices = selectedServices.filter((s) => s.id !== serviceToToggle.id);
    }

    onChange('connectedServices', { value: updatedServices });
  };
  const renderAdditionalFields = (service: IConnectedService) => {
    const serviceIndex = selectedServices.findIndex((s) => s.id === service.id);
    if (serviceIndex === -1) {
      return null;
    }

    switch (service.id) {
      case 1: {
        const allModules = dictionaryServices.length > 0 ? dictionaryServices[0].modules : [];
        const selectedModuleIds = selectedServices[serviceIndex]?.modules.map((m) => m.id) || [];
        const selectedModules = selectedServices[serviceIndex]?.modules || [];

        const handleModuleChange = (moduleId: number, isChecked: boolean) => {
          const currentSelectedModules = selectedServices[serviceIndex].modules || [];
          let newModules: ILookupValue[];

          if (isChecked) {
            const moduleToAdd = allModules.find((m) => m.id === moduleId);
            if (moduleToAdd) {
              newModules = [...currentSelectedModules, moduleToAdd];
            } else {
              newModules = currentSelectedModules;
            }
          } else {
            newModules = currentSelectedModules.filter((m) => m.id !== moduleId);
          }

          const newSelectedServices = [...selectedServices];
          newSelectedServices[serviceIndex] = {
            ...newSelectedServices[serviceIndex],
            modules: newModules,
          };

          onChange('connectedServices', { value: newSelectedServices });
        };

        const analyticsModuleRenderer = (itemProps: any) => {
          const module = itemProps.item as ILookupValue;
          const isChecked = selectedModuleIds.includes(module.id);

          const toggleCheckbox = (e: React.MouseEvent | React.ChangeEvent) => {
            e.preventDefault();
            e.stopPropagation();

            handleModuleChange(module.id, !isChecked);
          };

          return (
              <>
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div
                    className={styles.dropdownItem}
                    onClick={toggleCheckbox}
                >
                  <Checkbox
                      value={isChecked}
                      onChange={(e: CheckboxChangeEvent) => handleModuleChange(module.id, e.value)}
                  />
                  <label
                      className={styles.dropdownItemLabel}
                      style={{ cursor: 'pointer', flex: 1, marginLeft: '8px' }}
                  >
                    {module.name}
                  </label>
                </div>
              </>
          );
        };

        const buttonText = selectedModules.length > 0 ? selectedModules.map((module) => module.name).join(', ') : '';

        return (
          <>
            {allModules && (
              <div className={styles.configArea}>
                <label htmlFor={'analytics-dropdown'} className={'k-label'}>
                  NexPark Analytics Module{edit && <sup className='required-star'></sup>}
                </label>
                <Field
                  name={`connectedServices[${serviceIndex}].modules`}
                  component={FormDropdownMultiCheckbox}
                  validator={requiredArrayValidator}
                  required={edit}
                  disabled={!edit}
                  text={buttonText}
                  items={allModules}
                  itemRender={analyticsModuleRenderer}
                  svgIcon={caretAltDownIcon}
                />
              </div>
            )}
          </>
        );
      }
      case 2: {
        const isCityMissing = isSavedValueMissing(valueGetter('city'), cities);

        return (
          <>
            {cities && (
              <div className={styles.configArea}>
                <Field
                  name={'city'}
                  label='NexCity Instance'
                  component={edit ? FormDropDown : LookupTextFormField}
                  data={cities}
                  validator={requiredValidator}
                  textField='name'
                  dataItemKey='id'
                  required={true}
                />
                {isCityMissing && (
                  <div className={styles.warningBox}>
                      {WARNING_SERVICE_TEXT}
                  </div>
                )}
              </div>
            )}
          </>
        );
      }
      case 3: {
        const isNexParkInstanceMissing = isSavedValueMissing(valueGetter('nexParkInstance'), nexParkInstances);

        return (
          <>
            {nexParkInstances && (
              <div className={styles.configArea}>
                <Field
                  required={true}
                  name={'nexParkInstance'}
                  label='NexPark Instance'
                  validator={requiredValidator}
                  component={edit ? FormDropDown : LookupTextFormField}
                  data={nexParkInstances}
                  textField='name'
                  dataItemKey='id'
                />
                  {isNexParkInstanceMissing && (
                      <div className={styles.warningBox}>
                          {WARNING_SERVICE_TEXT}
                      </div>
                  )}
              </div>
            )}
          </>
        );
      }
      case 4: {
        // customer object doesn't have a nexResponseRegion field like city or nexParkInstance
        // here representing the view for nexResponseRegionId field with helper value
        // and prepare nexResponseRegion for backend mapping and saving
        const currentRegionId = valueGetter('nexResponseRegionId');
        const displayValue = nexResponseRegions.find(r => String(r.id) === String(currentRegionId));
        const isNexResponseRegionMissing = isSavedValueMissing(currentRegionId, nexResponseRegions);

        if (!edit) {
          return (
              <div className={styles.configArea}>
               <span className="k-label" style={{ fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                 NexResponse Region
               </span>
                  <div className="k-form-field-wrap">
              <span style={{ padding: '8px 0', display: 'block' }}>
            {displayValue?.name || 'N/A'}
          </span>
                </div>
                {isNexResponseRegionMissing && (<div className={styles.warningBox}>{WARNING_SERVICE_TEXT}</div>)}
              </div>
          );
        }

        return (
            <div className={styles.configArea}>
              <Field
                  name="nexResponseRegionId"
                  label="NexResponse Region"
                  required={true}
                  validator={requiredValidator}
                  component={FormDropDown}
                  data={nexResponseRegions}
                  textField="name"
                  dataItemKey="id"
                  valueMap={(item: any) => (item && item.id ? item.id : item)}
                  onChange={(e: any) => {
                    const selectedId = e.value;
                    const fullObject = nexResponseRegions.find(r => String(r.id) === String(selectedId));

                    onChange('nexResponseRegionId', { value: selectedId });
                    onChange('nexResponseRegion', { value: fullObject });
                  }}
              />
                {isNexResponseRegionMissing && (<div className={styles.warningBox}>{WARNING_SERVICE_TEXT}</div>)}
            </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      {dictionaryServices &&
        dictionaryServices.map((service) => {
          const isSelected = selectedServices.some((s) => s.id === service.id);

          return (
            <div key={service.id} className={styles.serviceBox}>
              <div className={styles.serviceHeader}>
                <Switch
                  checked={isSelected}
                  onChange={(event: SwitchChangeEvent) => handleServiceToggle(service, event.value)}
                  disabled={!edit}
                  onLabel={''}
                  offLabel={''}
                />
                <div className={styles.serviceInfo}>
                  <img src={connectedServiceIconService.getServiceIcon(service.id)} alt={'logo'} />
                </div>
              </div>
              {isSelected && renderAdditionalFields(service)}
            </div>
          );
        })}
    </div>
  );
};
